import React, { useEffect, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled, { keyframes } from 'styled-components';
import {
  FaFacebook,
  FaInstagram,
  FaLinkedin,
  FaTwitter,
  FaTiktok,
  FaYoutube,
  FaArrowUp,
  FaArrowDown,
  FaMinus,
  FaLink,
} from 'react-icons/fa';
import {
  ProfileCard,
  CardTitle,
  CardContent,
  SocialLinks,
  SocialLink,
  SocialIcon,
  FollowerCount,
} from './styles';
import { listSocialProfiles } from '../../services/platformService';

const mono = "'JetBrains Mono', 'Consolas', monospace";

const spin = keyframes`to { transform: rotate(360deg); }`;
const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  padding: 1rem;
  font-family: ${mono};
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);

  &::before {
    content: '';
    width: 12px;
    height: 12px;
    border: 2px solid rgba(0, 180, 216, 0.25);
    border-top-color: var(--btn-primary);
    border-radius: 50%;
    animation: ${spin} 0.9s linear infinite;
  }
`;

const Empty = styled.div`
  padding: 1rem;
  text-align: center;
  color: var(--text-disabled);
  font-size: 0.85rem;
  font-family: ${mono};
  letter-spacing: 0.04em;
`;

const Delta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-left: 0.5rem;
  font-family: ${mono};
  font-size: 0.65rem;
  letter-spacing: 0.04em;
  color: ${({ $dir }) =>
    $dir === 'up' ? 'var(--alert-success)' :
    $dir === 'down' ? 'var(--alert-error)' :
    'var(--text-disabled)'};

  svg { font-size: 0.55rem; }
`;

const PLATFORM_META = {
  facebook:  { icon: <FaFacebook />,  label: 'Facebook' },
  instagram: { icon: <FaInstagram />, label: 'Instagram' },
  linkedin:  { icon: <FaLinkedin />,  label: 'LinkedIn' },
  x:         { icon: <FaTwitter />,   label: 'X' },
  twitter:   { icon: <FaTwitter />,   label: 'X' },
  tiktok:    { icon: <FaTiktok />,    label: 'TikTok' },
  youtube:   { icon: <FaYoutube />,   label: 'YouTube' },
};

function formatCount(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(n);
}

function deltaInfo(latest, previous) {
  const a = latest?.followers;
  const b = previous?.followers;
  if (a == null || b == null) return { dir: 'flat', label: 'sin Δ' };
  const diff = a - b;
  if (diff === 0) return { dir: 'flat', label: '0' };
  const sign = diff > 0 ? '+' : '−';
  return {
    dir: diff > 0 ? 'up' : 'down',
    label: `${sign}${formatCount(Math.abs(diff))}`,
  };
}

const SocialMediaSection = () => {
  const [state, setState] = useState({ loading: true, error: null, items: [] });

  useEffect(() => {
    let cancelled = false;
    listSocialProfiles()
      .then((profiles) => {
        if (cancelled) return;
        // Solo redes propias (is_own===true) — el resto (incl. sin flag) son competidores
        const own = (profiles || []).filter((p) => p.is_own === true);
        setState({ loading: false, error: null, items: own });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          loading: false,
          error: friendlyError(err, 'Error'),
          items: [],
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <ProfileCard>
      <CardTitle>Redes Sociales · Monitoreo</CardTitle>
      <CardContent>
        {state.loading && <Loading>Cargando redes</Loading>}
        {!state.loading && state.error && (
          <Empty>No se pudo cargar · {state.error}</Empty>
        )}
        {!state.loading && !state.error && state.items.length === 0 && (
          <Empty>Sin perfiles registrados — agregar en Configuración</Empty>
        )}

        {!state.loading && !state.error && state.items.length > 0 && (
          <SocialLinks>
            {state.items.map((p) => {
              const meta = PLATFORM_META[p.platform] || { icon: <FaLink />, label: p.platform };
              const followers = p.latest?.followers;
              const delta = deltaInfo(p.latest, p.previous);
              const display =
                p.display_name ||
                (p.platform === 'instagram' || p.platform === 'tiktok' ? `@${p.handle}` : p.handle);

              return (
                <SocialLink
                  key={p.id}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={p.url}
                >
                  <SocialIcon>{meta.icon}</SocialIcon>
                  <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <span
                      style={{
                        color: 'white',
                        fontSize: '0.9rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {display}
                    </span>
                    <span
                      style={{
                        fontSize: '0.68rem',
                        letterSpacing: '0.2em',
                        textTransform: 'uppercase',
                        color: 'var(--text-disabled)',
                        fontFamily: mono,
                      }}
                    >
                      {meta.label}
                    </span>
                  </div>
                  <FollowerCount>
                    {formatCount(followers)}
                    {followers != null && (
                      <Delta $dir={delta.dir}>
                        {delta.dir === 'up' && <FaArrowUp />}
                        {delta.dir === 'down' && <FaArrowDown />}
                        {delta.dir === 'flat' && <FaMinus />}
                        {delta.label}
                      </Delta>
                    )}
                  </FollowerCount>
                </SocialLink>
              );
            })}
          </SocialLinks>
        )}
      </CardContent>
    </ProfileCard>
  );
};

export default SocialMediaSection;
